import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Users, DollarSign, TrendingUp, Activity, AlertTriangle, Phone, Clock,
  ArrowUpRight, ArrowDownRight, Loader2, Bell, UserPlus, Shield, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(val) { return `$${(val || 0).toFixed(2)}`; }

const ALERT_STYLES = {
  error: { bg: "bg-red-500/10 border-red-500/20", icon: AlertTriangle, color: "text-red-400" },
  warning: { bg: "bg-amber-500/10 border-amber-500/20", icon: AlertTriangle, color: "text-amber-400" },
  info: { bg: "bg-blue-500/10 border-blue-500/20", icon: Bell, color: "text-blue-400" },
};

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [fixingStuck, setFixingStuck] = useState(false);
  const isInitialLoad = React.useRef(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, alertRes, costRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/alerts`),
        axios.get(`${API}/admin/cost-tracker`),
        new Promise(r => setTimeout(r, 600)),
      ]);
      setData(dashRes.data);
      setAlerts(alertRes.data.alerts || []);
      setCostData(costRes.data);
      if (!isInitialLoad.current) toast.success("Dashboard refreshed");
      isInitialLoad.current = false;
    } catch (e) {
      toast.error("Failed to load admin dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFixStuck = async () => {
    setFixingStuck(true);
    try {
      const res = await axios.post(`${API}/admin/fix-stuck-calls`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error("Failed to fix stuck calls");
    } finally {
      setFixingStuck(false);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const statCards = [
    { label: "Minutes Purchased", value: data?.total_credits_purchased || 0, icon: TrendingUp, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Minutes Used", value: data?.total_credits_used || 0, icon: Activity, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Total Users", value: data?.total_users || 0, icon: Users, color: "text-violet-400", bg: "bg-violet-500/10" },
    { label: "Active Users", value: data?.active_users || 0, icon: UserPlus, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Calls Today", value: data?.calls_today || 0, icon: Phone, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { label: "Total Calls", value: (data?.calls_total || 0).toLocaleString(), icon: Activity, color: "text-green-400", bg: "bg-green-500/10" },
    { label: "0 Minutes", value: data?.zero_credit_users || 0, icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10" },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#0D1117] border border-slate-700 rounded-lg px-3 py-2 text-xs">
        <p className="text-slate-400 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-mono">
            {p.name}: {typeof p.value === "number" ? p.value.toFixed(4) : p.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
            Admin Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Platform overview, revenue & user insights</p>
        </div>
        <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all self-start sm:self-auto" data-testid="refresh-admin-dashboard">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.filter(a => a.severity === "high").map((a, i) => {
            const style = ALERT_STYLES[a.type] || ALERT_STYLES.info;
            const isStuck = a.title === "Stuck Analyses";
            return (
              <div key={i} className={`flex items-center justify-between px-4 py-3 rounded-xl border ${style.bg}`}>
                <div className="flex items-center gap-3">
                  <style.icon className={`h-4 w-4 ${style.color} flex-shrink-0`} />
                  <div>
                    <span className={`text-sm font-medium ${style.color}`}>{a.title}: </span>
                    <span className="text-sm text-slate-300">{a.message}</span>
                  </div>
                </div>
                {isStuck && (
                  <button
                    onClick={handleFixStuck}
                    disabled={fixingStuck}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-xs font-medium rounded-lg transition-all disabled:opacity-50"
                    data-testid="fix-stuck-calls"
                  >
                    {fixingStuck ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    {fixingStuck ? "Fixing..." : "Fix Now"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3">
        {statCards.map((c) => (
          <Card key={c.label} className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="p-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{c.label}</span>
                <div className={`p-1 rounded-md ${c.bg}`}><c.icon className={`h-3 w-3 ${c.color}`} /></div>
              </div>
              <p className={`text-lg font-bold ${c.color} font-mono`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Chart */}
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-400" /> Revenue (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.revenue_chart || []}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#revGrad)" strokeWidth={2} name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Signups Chart */}
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><UserPlus className="h-4 w-4 text-violet-400" /> User Signups (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.signup_chart || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Signups" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Tracker */}
      {costData && (
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-400" /> AI Cost Tracker
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Formula Display */}
            <div className="flex items-center gap-2 flex-wrap text-xs bg-slate-950/50 rounded-lg px-3 py-2.5 border border-slate-800/30">
              <span className="text-slate-500">Logic:</span>
              <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 font-mono">Real Emergent Cost</span>
              <span className="text-slate-600">x</span>
              <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-mono">{costData.settings?.markup_multiplier}x</span>
              <span className="text-slate-600">=</span>
              <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 font-mono">User Charge</span>
              <span className="text-slate-600 ml-2">|</span>
              <span className="text-slate-500 ml-2">1 call minute = 1 minute deducted</span>
            </div>
            
            {/* Cost Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {[
                { label: "Total Minutes Charged", value: `${costData.totals?.total_minutes_charged?.toFixed(1) || "0"} min`, color: "text-green-400", bg: "bg-green-500/10" },
                { label: "Calls Tracked", value: costData.totals?.total_calls || 0, color: "text-cyan-400", bg: "bg-cyan-500/10" },
                { label: "Minutes Analyzed", value: costData.totals?.total_minutes || 0, color: "text-violet-400", bg: "bg-violet-500/10" },
              ].map((c) => (
                <div key={c.label} className={`${c.bg} rounded-lg p-2.5 border border-slate-800/30`}>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider">{c.label}</p>
                  <p className={`text-sm font-bold font-mono ${c.color} mt-0.5`}>{c.value}</p>
                </div>
              ))}
            </div>

            {/* Daily Profit Chart */}
            {costData.daily_chart?.length > 0 && (
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={costData.daily_chart}>
                    <defs>
                      <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="emergent_cost" stroke="#ef4444" fill="url(#costGrad)" strokeWidth={1.5} name="Your Cost" />
                    <Area type="monotone" dataKey="profit" stroke="#22c55e" fill="url(#profitGrad)" strokeWidth={2} name="Your Profit" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {/* Recent Calls Cost Table */}
            {costData.recent_calls?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/50">
                      <th className="text-left text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">Date</th>
                      <th className="text-left text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">Publisher</th>
                      <th className="text-right text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">Duration</th>
                      <th className="text-right text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">Emergent Cost</th>
                      <th className="text-right text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">User Charged</th>
                      <th className="text-right text-[9px] text-slate-500 uppercase tracking-wider font-medium px-3 py-2">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costData.recent_calls.map((c, i) => (
                      <tr key={c.id || i} className="border-b border-slate-800/20 hover:bg-slate-800/15 transition">
                        <td className="px-3 py-2 text-[11px] text-slate-400">{new Date(c.call_date).toLocaleDateString()}</td>
                        <td className="px-3 py-2 text-[11px] text-slate-300">{c.publisher_name || "—"}</td>
                        <td className="px-3 py-2 text-right text-[11px] font-mono text-slate-400">{Math.round((c.call_duration || 0) / 60)}m</td>
                        <td className="px-3 py-2 text-right text-[11px] font-mono text-red-400">{(c.emergent_cost || 0).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right text-[11px] font-mono text-green-400">{(c.credits_charged || 0).toFixed(1)} min</td>
                        <td className="px-3 py-2 text-right text-[11px] font-mono text-emerald-400">{(c.profit || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {!costData.recent_calls?.length && (
              <p className="text-xs text-center text-slate-500 py-4">No cost data yet. Cost tracking starts with new call analyses.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Revenue Users */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-amber-400" /> Top Revenue Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800/50">
                  <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">#</th>
                  <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">User</th>
                  <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">Company</th>
                  <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">Used</th>
                  <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">Purchased</th>
                  <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">Balance</th>
                  <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2.5">Analyzed</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_users || []).map((u, i) => (
                  <tr
                    key={u.id}
                    className="border-b border-slate-800/20 hover:bg-slate-800/20 cursor-pointer transition"
                    onClick={() => navigate(`/user-management/${u.id}`)}
                    data-testid={`top-user-${i}`}
                  >
                    <td className="px-4 py-2.5 text-xs text-slate-500">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <p className="text-sm text-slate-200">{u.name || "—"}</p>
                      <p className="text-[10px] text-slate-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">{u.company || "—"}</td>
                    <td className="px-4 py-2.5 text-right text-sm font-mono text-amber-400">{(u.total_credits_used || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                    <td className="px-4 py-2.5 text-right text-sm font-mono text-green-400/70">{(u.total_credits_purchased || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                    <td className="px-4 py-2.5 text-right text-sm font-mono text-slate-300">{(u.credits || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                    <td className="px-4 py-2.5 text-right text-sm font-mono text-cyan-400/70">{(u.total_minutes_used || 0).toFixed(1)}</td>
                  </tr>
                ))}
                {(!data?.top_users || data.top_users.length === 0) && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500 text-sm">No user revenue data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* All Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Bell className="h-4 w-4 text-amber-400" /> System Alerts ({alerts.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a, i) => {
              const style = ALERT_STYLES[a.type] || ALERT_STYLES.info;
              return (
                <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${style.bg}`}>
                  <style.icon className={`h-4 w-4 ${style.color} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs font-semibold ${style.color}`}>{a.title}</span>
                    <p className="text-xs text-slate-400">{a.message}</p>
                  </div>
                  <Badge className={`text-[9px] ${a.severity === "high" ? "bg-red-500/15 text-red-400" : a.severity === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-blue-500/15 text-blue-400"}`}>
                    {a.severity}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
